/**
 * Input Component
 *
 * A flexible input component with validation states and accessibility features.
 * Supports various input types and integrates with form libraries.
 */

import React, { forwardRef } from 'react';
import { cn } from '@utils/cn';

export type InputVariant = 'default' | 'filled' | 'outlined';
export type InputSize = 'sm' | 'md' | 'lg';
export type InputState = 'default' | 'error' | 'success' | 'warning';

export interface InputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'size' | 'success' | 'error' | 'warning'
  > {
  variant?: InputVariant;
  size?: InputSize;
  state?: InputState;
  label?: string;
  helperText?: string;
  errorText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  required?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      variant = 'default',
      size = 'md',
      state = 'default',
      label,
      helperText,
      errorText,
      leftIcon,
      rightIcon,
      fullWidth = false,
      required = false,
      className,
      id,
      ...props
    },
    ref
  ) => {
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
    const hasError = state === 'error' || !!errorText;
    const hasSuccess = state === 'success';
    const hasWarning = state === 'warning';

    // Base input classes - Modern B2B Professional with thinner borders
    const baseClasses = cn(
      'block w-full rounded-lg border transition-all duration-200',
      'focus:outline-none focus:ring-1 focus:ring-offset-0',
      'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed',
      'dark:disabled:bg-gray-800 dark:disabled:text-gray-400',
      'shadow-sm focus:shadow-md',
      fullWidth && 'w-full'
    );

    // Variant classes
    const variantClasses = {
      default: cn(
        'border-gray-200 bg-white text-gray-900',
        'focus:border-transparent focus:ring-0 focus:outline-none',
        'focus:input-gradient-border',
        'dark:border-gray-700 dark:bg-gray-800 dark:text-white',
        'dark:focus:border-transparent dark:focus:ring-0 dark:focus:outline-none',
        'dark:focus:input-gradient-border'
      ),
      filled: cn(
        'border-transparent bg-gray-100 text-gray-900',
        'focus:border-transparent focus:ring-0 focus:bg-white focus:outline-none',
        'focus:input-gradient-border',
        'dark:bg-gray-700 dark:text-white',
        'dark:focus:bg-gray-600 dark:focus:border-transparent dark:focus:ring-0 dark:focus:outline-none',
        'dark:focus:input-gradient-border'
      ),
      outlined: cn(
        'border border-gray-200 bg-transparent text-gray-900',
        'focus:border-transparent focus:ring-0 focus:outline-none',
        'focus:input-gradient-border',
        'dark:border-gray-700 dark:text-white',
        'dark:focus:border-transparent dark:focus:ring-0 dark:focus:outline-none',
        'dark:focus:input-gradient-border'
      ),
    };

    // Size classes
    const sizeClasses = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-3 py-2 text-sm',
      lg: 'px-4 py-3 text-base',
    };

    // State classes - only gradient border on focus, no colored borders
    const stateClasses = cn(
      hasError && 'border-red-200 dark:border-red-800',
      hasSuccess && 'border-green-200 dark:border-green-800',
      hasWarning && 'border-yellow-200 dark:border-yellow-800'
    );

    // Icon container classes
    const iconContainerClasses = 'absolute inset-y-0 flex items-center pointer-events-none';
    const leftIconClasses = 'left-0 pl-3';
    const rightIconClasses = 'right-0 pr-3';

    // Input with icons padding
    const inputPaddingClasses = cn(leftIcon && 'pl-10', rightIcon && 'pr-10');

    return (
      <div className={cn('relative', fullWidth && 'w-full')}>
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            {label}
            {required && <span className="text-error-500 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          {leftIcon && <div className={cn(iconContainerClasses, leftIconClasses)}>{leftIcon}</div>}

          <input
            ref={ref}
            id={inputId}
            className={cn(
              baseClasses,
              variantClasses[variant],
              sizeClasses[size],
              stateClasses,
              inputPaddingClasses,
              className
            )}
            aria-invalid={hasError}
            aria-describedby={
              hasError && errorText
                ? `${inputId}-error`
                : helperText
                  ? `${inputId}-helper`
                  : undefined
            }
            {...(() => {
              const {
                success: _success,
                error: _error,
                warning: _warning,
                ...restProps
              } = props as Record<string, unknown>;
              // Suppress unused variable warnings
              void _success;
              void _error;
              void _warning;
              return restProps;
            })()}
          />

          {rightIcon && (
            <div className={cn(iconContainerClasses, rightIconClasses)}>{rightIcon}</div>
          )}
        </div>

        {/* Helper text and error messages */}
        {hasError && errorText && (
          <p id={`${inputId}-error`} className="mt-1 text-sm text-error-600 dark:text-error-400">
            {errorText}
          </p>
        )}

        {!hasError && helperText && (
          <p id={`${inputId}-helper`} className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input };
